/**
 * Cook mode (ROADMAP §3 WS-19) — a dependency-free "timer done" chime: three short
 * oscillator beeps via WebAudio, generated on the fly (no bundled audio asset, per the
 * project's "no big players / vendor nothing we can generate" north star). Best-effort:
 * browsers that block audio without a user gesture, or lack WebAudio entirely, just get the
 * visual flash + notification instead.
 */
export function playChime(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beepAt = (offsetSeconds: number): void => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      const start = ctx.currentTime + offsetSeconds;
      const end = start + 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.35, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(end + 0.02);
    };
    beepAt(0);
    beepAt(0.26);
    beepAt(0.52);
    // Best-effort cleanup once the beeps have finished playing.
    setTimeout(() => void ctx.close(), 1200);
  } catch {
    // Audio is a nicety here, never load-bearing.
  }
}

/** Best-effort local Notification for a finished timer — feature-detected, never requests
 * permission itself (the caller should have asked once, e.g. on first timer start). */
export function notifyTimerDone(name: string): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    new Notification('Timer done', { body: name, tag: `cook-timer-${name}` });
  } catch {
    // Notifications are a nicety here, never load-bearing.
  }
}
